import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { renderReact } from "../src/lib/ssrHydrator";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps"; // eslint-disable-line import/no-unresolved
import SmallArticleBox from "../src/components/SmallArticleBox";
import Article from "../src/entities/article";

const isToday = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() === today.getTime();
};

const isTomorrow = (date) => {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() === tomorrow.getTime();
};

const isThisWeek = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  const thisWeekEnd = new Date(today);
  const dayToSunday = 7 - today.getDay();
  thisWeekEnd.setDate(today.getDate() + dayToSunday);

  return d > today && d <= thisWeekEnd;
};

const isNextWeek = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const thisWeekEnd = new Date(today);
  const dayToSunday = 7 - today.getDay();
  thisWeekEnd.setDate(today.getDate() + dayToSunday);

  const nextWeekEnd = new Date(thisWeekEnd);
  nextWeekEnd.setDate(thisWeekEnd.getDate() + 7);

  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  return d > thisWeekEnd && d <= nextWeekEnd;
};

const isWednesday = (date) => {
  const d = new Date(date);
  return d.getDay() === 3; // 3は水曜日
};

const sortAndGroupLocationsByDate = (locationData, articlesHash) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  const thisWeekWednesday = new Date(now);
  const dayToWednesday = (3 - now.getDay() + 7) % 7;
  thisWeekWednesday.setDate(now.getDate() + dayToWednesday);

  const nextWeekWednesday = new Date(thisWeekWednesday);
  nextWeekWednesday.setDate(thisWeekWednesday.getDate() + 7);

  const isTodayWednesday = now.getDay() === 3;
  const isTomorrowWednesday = tomorrow.getDay() === 3;

  const formatDateForTitle = (date, suffix = "") => {
    if (!date) return "日程なし";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const weekDays = ["日", "月", "火", "水", "木", "金", "土"];
    const weekDay = weekDays[d.getDay()];
    return `${year}年${month}月${day}日 ${weekDay}曜日${suffix}`;
  };

  // NOTE グループタイトルの初期設定（これは既存のものをベースに、アイテムがない場合も考慮）
  const groups = {
    todayWednesday: {
      title: isTodayWednesday
        ? formatDateForTitle(now, "（本日開催）")
        : "本日開催",
      items: [],
    },
    tomorrowWednesday: {
      title: isTomorrowWednesday
        ? formatDateForTitle(tomorrow, "（明日開催）")
        : "明日開催",
      items: [],
    },
    thisWeekWednesday: {
      title: formatDateForTitle(
        thisWeekWednesday,
        isThisWeek(thisWeekWednesday) ? "（今週開催）" : ""
      ),
      items: [],
    },
    nextWeekWednesday: {
      title: formatDateForTitle(
        nextWeekWednesday,
        isNextWeek(nextWeekWednesday) ? "（来週開催）" : ""
      ),
      items: [],
    },
    otherWednesday: { title: "その他の水曜日開催", items: [] },
    otherDays: { title: "その他曜日開催", items: [] },
    noDates: { title: "日程なし", items: [] },
  };

  locationData.forEach((location) => {
    const article = articlesHash[location.article_id];

    if (!article) {
      groups.noDates.items.push({
        ...location,
        formattedDate: "日程なし",
        date: new Date(8640000000000000), // NOTE: ソート用: 非常に未来の日付
        _dateKeySuffix: "noarticle", // NOTE: React key 用のサフィックス
      });
      return;
    }

    if (!article.activity_dates || article.activity_dates.length === 0) {
      groups.noDates.items.push({
        ...location,
        formattedDate: "日程なし",
        date: new Date(8640000000000000),
        _dateKeySuffix: "noactdates",
      });
      return;
    }

    const futureActivityDates = article.activity_dates.filter(
      (activityDate) => {
        if (!activityDate || !activityDate.start_date) return false;
        const date = new Date(activityDate.start_date);
        date.setHours(0, 0, 0, 0);
        return date >= now; // 今日以降の日付のみを処理
      }
    );

    if (futureActivityDates.length === 0) {
      // NOTE: 未来の活動日がない場合 (全て過去の日付、または日付情報が不正など)
      groups.noDates.items.push({
        ...location,
        formattedDate: "過去開催または日程なし",
        date: new Date(
          Math.max(
            ...article.activity_dates.map((ad) =>
              new Date(ad.start_date).getTime()
            )
          ) || 0
        ),
        _dateKeySuffix: "pastordatesinvalid",
      });
      return;
    }

    futureActivityDates.forEach((activityDate, dateIndex) => {
      const nextDate = new Date(activityDate.start_date);
      nextDate.setHours(0, 0, 0, 0);

      let suffix = "";
      if (isWednesday(nextDate)) {
        if (isToday(nextDate)) suffix = "（本日開催）";
        else if (isTomorrow(nextDate)) suffix = "（明日開催）";
        else if (isThisWeek(nextDate)) suffix = "（今週開催）";
        else if (isNextWeek(nextDate)) suffix = "（来週開催）";
      }

      const d = new Date(nextDate);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const dayVal = d.getDate();
      const weekDays = ["日", "月", "火", "水", "木", "金", "土"];
      const weekDay = weekDays[d.getDay()];
      const formattedDateStr = `${year}年${month}月${dayVal}日 ${weekDay}曜日${suffix}`;

      const enrichedLocation = {
        ...location,
        formattedDate: formattedDateStr,
        date: nextDate,
        _dateKeySuffix: dateIndex, // NOTE: React key のユニーク性のため
      };

      if (isWednesday(nextDate)) {
        if (isToday(nextDate) && isTodayWednesday)
          groups.todayWednesday.items.push(enrichedLocation);
        else if (isTomorrow(nextDate) && isTomorrowWednesday)
          groups.tomorrowWednesday.items.push(enrichedLocation);
        else if (nextDate.getTime() === thisWeekWednesday.getTime())
          groups.thisWeekWednesday.items.push(enrichedLocation);
        else if (nextDate.getTime() === nextWeekWednesday.getTime())
          groups.nextWeekWednesday.items.push(enrichedLocation);
        else groups.otherWednesday.items.push(enrichedLocation);
      } else {
        groups.otherDays.items.push(enrichedLocation);
      }
    });
  });

  Object.keys(groups).forEach((key) => {
    groups[key].items.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    if (key !== "noDates") {
      if (groups[key].items.length > 0) {
        let suffix = "";
        let baseDateForTitle = groups[key].items[0].date; // デフォルトはアイテムの最初の日付

        if (key === "todayWednesday") {
          if (!isTodayWednesday) {
            groups[key].title = "本日開催";
          } // 今日が水曜でなければ汎用タイトル
          else {
            baseDateForTitle = now;
            suffix = "（本日開催）";
            groups[key].title = formatDateForTitle(baseDateForTitle, suffix);
          }
        } else if (key === "tomorrowWednesday") {
          if (!isTomorrowWednesday) {
            groups[key].title = "明日開催";
          } else {
            baseDateForTitle = tomorrow;
            suffix = "（明日開催）";
            groups[key].title = formatDateForTitle(baseDateForTitle, suffix);
          }
        } else if (key === "thisWeekWednesday") {
          baseDateForTitle = thisWeekWednesday;
          suffix = isThisWeek(thisWeekWednesday) ? "（今週開催）" : "";
          groups[key].title = formatDateForTitle(baseDateForTitle, suffix);
        } else if (key === "nextWeekWednesday") {
          baseDateForTitle = nextWeekWednesday;
          suffix = isNextWeek(nextWeekWednesday) ? "（来週開催）" : "";
          groups[key].title = formatDateForTitle(baseDateForTitle, suffix);
        } else if (key === "otherWednesday") {
          groups[key].title = "その他の水曜日開催"; // 固定
        } else if (key === "otherDays") {
          groups[key].title = "その他曜日開催"; // 固定
        }
      } else {
        // NOTE: アイテムがない場合、初期設定されたタイトルをそのまま使用
        // (例: isTodayWednesday=false なら groups.todayWednesday.title = "本日開催")
      }
    }
    // NOTE: noDates グループのタイトルは "日程なし" で固定
  });
  return groups;
};

// API key should be provided through server-side rendering
// See config/initializers/geocoder.rb for API key management
const DEFAULT_CENTER = { lat: 35.6811441, lng: 139.7644865 }; // 東京駅の座標
const DEFAULT_ZOOM = 11;

// NOTE: 重なっているマーカーは少しだけずらすようにする
const getOffsetPosition = (locations, currentLocation, threshold = 0.0001) => {
  // NOTE: マーカーの分散具合を指定
  const gridSize = threshold;

  const cellKey = `${Math.floor(currentLocation.latitude / gridSize)}_${Math.floor(currentLocation.longitude / gridSize)}`;

  let countInSameCell = 0;
  for (const loc of locations) {
    const locCellKey = `${Math.floor(loc.latitude / gridSize)}_${Math.floor(loc.longitude / gridSize)}`;

    if (locCellKey === cellKey) {
      if (loc === currentLocation) {
        break;
      }
      countInSameCell++;
    }
  }

  // NOTE: 重なっているマーカーがない場合はそのままの位置を返す
  if (countInSameCell === 0) {
    return {
      lat: currentLocation.latitude,
      lng: currentLocation.longitude
    };
  }

  // NOTE: スパイラル状にオフセットを計算（時計回り）
  const angle = (countInSameCell % 8) * (Math.PI / 4);
  const radius = (Math.floor(countInSameCell / 8) + 1) * threshold * 1.5; // 距離を徐々に増やす

  return {
    lat: currentLocation.latitude + Math.sin(angle) * radius,
    lng: currentLocation.longitude + Math.cos(angle) * radius
  };
};

function ArticleMapViewer({
  articles,
  locations,
  articles_hash,
  company_name,
}) {
  const [articleData, setArticleData] = useState([]);
  const [articlesHashData, setArticlesHashData] = useState({});
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const [selectedLocation, setSelectedLocation] = useState(null);
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const [hoveredLocationId, setHoveredLocationId] = useState(null); // ホバー状態を管理
  const [selectedLocationId, setSelectedLocationId] = useState(null); // 選択状態を管理
  const [mapRef, setMapRef] = useState(null);
  const mapContainerRef = useRef(null);
  const deviceTypeRef = useRef(null); // PCとSPの判定結果を保持するref

  // Function to get company name from props or URL
  const getCompanyName = () => {
    if (company_name) return company_name;
    const pathParts = window.location.pathname.split("/");
    return pathParts[2]; // /companies/:name/2025/summer_children
  };

  // PCかSPかを判定する関数（画面読み込み時に一度だけ実行）
  useEffect(() => {
    deviceTypeRef.current = document.body.classList.contains("pc")
      ? "pc"
      : "sp";
  }, []);

  // スクロール処理を行う共通関数
  const scrollToLocation = (locationId) => {
    const listItem = document.querySelector(
      `.jsLocationItem[data-location-id="${locationId}"]`
    );
    const container = document.querySelector(".jsLocationListContainer");

    if (!listItem || !container) return;

    // PCとSPで異なるスクロール挙動を実装
    if (deviceTypeRef.current === "pc") {
      // PC: コンテナ内のスクロールのみを行う（画面スクロールしない）
      const containerRect = container.getBoundingClientRect();
      const itemRect = listItem.getBoundingClientRect();
      const scrollTop =
        container.scrollTop +
        (itemRect.top - containerRect.top) -
        containerRect.height / 2 +
        itemRect.height / 2;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    } else {
      // SP: コンテナ内スクロールと画面スクロールの両方を行う

      // 1. まずコンテナ内で選択されたアイテムにスクロール
      const containerRect = container.getBoundingClientRect();
      const itemRect = listItem.getBoundingClientRect();
      const scrollTop =
        container.scrollTop +
        (itemRect.top - containerRect.top) -
        containerRect.height / 2 +
        itemRect.height / 2;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });

      // 2. 少し遅らせてからlocationListContainerの下部が画面下部に合うようにスクロール
      setTimeout(() => {
        // コンテナの高さと位置を再計算（スクロール後の値を取得するため）
        const updatedContainerRect = container.getBoundingClientRect();

        // コンテナの下部が画面の下部より下にある場合（見えていない場合）のみスクロール
        if (updatedContainerRect.bottom > window.innerHeight) {
          const bottomPosition =
            updatedContainerRect.bottom + window.pageYOffset;
          const windowHeight = window.innerHeight;

          window.scrollTo({
            top: bottomPosition - windowHeight,
            behavior: "smooth",
          });
        }
      }, 300); // コンテナ内スクロールが完了する時間を考慮して遅延
    }
  };

  // マーカーの色を決定する関数
  const getMarkerColor = (location) => {
    const locationId = `${location.article_id}-${location.index}`;

    // hover状態 または 選択状態の場合は青色に
    if (hoveredLocationId === locationId || selectedLocationId === locationId) {
      return "#4285F4"; // 選択時/ホバー時の青色
    }

    return "#ff5722"; // デフォルトの赤色
  };

  // Load article and location data
  useEffect(() => {
    try {
      setLoading(true);

      const processedArticles =
        articles && articles.length > 0
          ? articles.map((article) => ({
              ...article,
              id: article.id ? article.id.toString() : null,
            }))
          : [];

      let hashData = {};
      if (
        articles_hash &&
        typeof articles_hash === "object" &&
        Object.keys(articles_hash).length > 0
      ) {
        hashData = articles_hash;
      } else {
        processedArticles.forEach((article) => {
          if (article && article.id) {
            hashData[article.id] = article;
          }
        });
      }

      setArticleData(processedArticles);
      setArticlesHashData(hashData);

      // Process location data
      if (locations && locations.length > 0) {
        const processedLocations = locations.map((location) => ({
          ...location,
          article_id: location.article_id
            ? location.article_id.toString()
            : null,
        }));

        setLocationData(processedLocations);
        setLoading(false);
      } else {
        setLoading(false);
        setError("表示できる記事がありません");
      }
    } catch (err) {
      setError("記事データの読み込みに失敗しました");
      setLoading(false);
    }
  }, [articles, locations, articles_hash]);

  // Mapが読み込まれた時に詳細な設定を適用
  const handleMapLoad = (map) => {
    setMapRef(map);

    // GoogleMapsのネイティブコントロールを直接設定
    if (map) {
      // すべてのコントロールを非表示に設定
      map.setOptions({
        panControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        zoomControl: true,
        rotateControl: false,
        scaleControl: false,
        zoomControlOptions: {
          position: window.google?.maps?.ControlPosition?.RIGHT_BOTTOM || 7,
        },
      });
    }
  };

  // Find article by ID
  const getArticleById = (articleId) => {
    if (!articleId) return null;

    const idStr = articleId.toString();

    if (articlesHashData[idStr]) {
      return articlesHashData[idStr];
    }

    return articleData.find((item) => item && item.id && item.id === idStr);
  };

  // Handle marker click
  const handleMarkerClick = (location) => {
    const article = getArticleById(location.article_id);

    if (article) {
      // Set minimal required properties for PCArticleBox if not present
      if (!article.working_form_or_kind) {
        article.working_form_or_kind = ["ボランティア"];
      }

      if (!article.updated_date) {
        article.updated_date = new Date().toLocaleDateString("ja-JP");
      }

      if (mapRef) {
        mapRef.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });

        if (mapRef.getZoom() < 12) {
          mapRef.setZoom(14);
        }
      }

      const locationId = `${location.article_id}-${location.index || 0}`;
      setSelectedLocationId(locationId);
      setHoveredLocationId(locationId); // SP表示のために追加

      // カスタムスクロール処理
      setTimeout(() => {
        scrollToLocation(locationId);
      }, 100);

      setSelectedArticle(article);
      setSelectedLocation(location);
    }
  };

  const handleClearSelection = () => {
    setSelectedArticle(null);
    setSelectedLocation(null);
    setSelectedLocationId(null);
    setHoveredLocationId(null); // 選択解除時にはhover状態も解除
  };

  // Handle article click
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const handleArticleClick = () => {
    if (selectedArticle) {
      window.location.href = company_name
        ? `/companies/${getCompanyName()}/articles/${selectedArticle.id}`
        : `/articles/${selectedArticle.id}`;
    }
  };
  /* eslint-enable @typescript-eslint/no-unused-vars */

  if (loading) {
    return <div className="loading jsLoading">読み込み中...</div>;
  }

  if (error) {
    return <div className="error jsError">{error}</div>;
  }

  const locationGroups = sortAndGroupLocationsByDate(
    locationData,
    articlesHashData
  );

    // ホバーされている location オブジェクトを取得
  const activeHoveredLocation = hoveredLocationId
    ? locationData.find(loc => `${loc.article_id}-${loc.index || 0}` === hoveredLocationId)
    : null;

  let hoveredArticle = null;
  if (activeHoveredLocation) {
    hoveredArticle = getArticleById(activeHoveredLocation.article_id);
  }

  // InfoWindow の表示位置を計算 (getOffsetPosition を利用)
  const infoWindowPosition = activeHoveredLocation
    ? getOffsetPosition(locationData, activeHoveredLocation)
    : null;

  return (
    <div className="articleMapViewer m_articleMapViewer">
      <h3 className="mapTitle">子ども支援活動マップ</h3>

      <div className="mapWithLocationList">
        {/* ロケーション一覧（PC表示では左側に表示） */}
        <div className="locationListContainer jsLocationListContainer">
          <h3 className="locationListTitle">活動場所一覧</h3>
          <div className="locationList">
            {Object.values(locationGroups).some(
              (group) => group.items.length > 0
            ) ? (
              <div className="locationGroups">
                {Object.entries(locationGroups).map(([key, group]) => {
                  if (group.items.length === 0) return null;

                  return (
                    <section key={key} className="locationGroup">
                      <h3 className="sectionTitle">{group.title}</h3>
                      <ul className="locationItems">
                        {group.items.map((locationItem) => {
                          const article = getArticleById(
                            locationItem.article_id
                          );
                          if (!article) return null;

                          const newArticle = {
                            ...article,
                            _modified_text_for_views: {
                              ...(article._modified_text_for_views || {}),
                              display_areas: locationItem.spot_name,
                            },
                          };

                          const locationIdForMarkerSync = `${
                            locationItem.article_id
                          }-${locationItem.index || 0}`;

                          const isSelected =
                            selectedLocationId === locationIdForMarkerSync;
                          const isHovered =
                            hoveredLocationId === locationIdForMarkerSync;

                          return (
                            <li
                              key={`location-${locationItem.article_id}-${
                                locationItem.index || 0
                              }-${locationItem._dateKeySuffix}`}
                              data-location-id={locationIdForMarkerSync}
                              className={`locationItem jsLocationItem ${
                                isHovered ? "isHovered" : ""
                              } ${isSelected ? "isSelected" : ""}`}
                              onMouseEnter={() => {
                                setHoveredLocationId(locationIdForMarkerSync);
                              }}
                              onMouseLeave={() => {
                                if (
                                  selectedLocationId !== locationIdForMarkerSync
                                ) {
                                  setHoveredLocationId(null);
                                }
                              }}
                            >
                              <div
                                className="locationItemInner"
                                onClick={() => handleMarkerClick(locationItem)} // NOTE: locationItem は enrichedLocation で、必要な情報 (lat,lng,article_id,index) を含んでいる
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    handleMarkerClick(locationItem);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                <SmallArticleBox
                                  article={new Article(newArticle)}
                                  companyName={company_name}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}
              </div>
            ) : (
              <p className="noLocations">表示できる活動場所がありません</p>
            )}
          </div>
        </div>

        {/* マップコンテナ */}
        <div className="mapContainer jsMapContainer" ref={mapContainerRef}>
          <APIProvider apiKey={window.googleMapsApiKey || ""}>
            <Map
              defaultCenter={DEFAULT_CENTER}
              defaultZoom={DEFAULT_ZOOM}
              mapId="summer-children-map"
              gestureHandling="greedy"
              onLoad={handleMapLoad}
              onClick={handleClearSelection}
              disableDefaultUI={true}
              zoomControl={true}
              zoomControlOptions={{
                position: 9,
              }}
            >
              {locationData.map((location) => {
                const locationId = `${location.article_id}-${
                  location.index || 0
                }`;
                const isSelected = selectedLocationId === locationId;
                const isHovered = hoveredLocationId === locationId; // この isHovered はマーカー自身のスタイル制御用
                const position = getOffsetPosition(locationData, location);

                return (
                  <Marker
                    key={`marker-${location.article_id}-${location.latitude}-${
                      location.longitude
                    }-${
                      location.index ||
                      Math.random().toString(36).substring(2, 7)
                    }`}
                    position={position}
                    onClick={() => handleMarkerClick(location)}
                    onMouseOver={() => {
                      setHoveredLocationId(locationId);
                      setSelectedLocationId(locationId);

                      if (deviceTypeRef.current === "pc") {
                        setTimeout(() => {
                          const listItem = document.querySelector(
                            `.jsLocationItem[data-location-id="${locationId}"]`
                          );
                          const container = document.querySelector(
                            ".jsLocationListContainer"
                          );
                          if (listItem && container) {
                            const containerRect =
                              container.getBoundingClientRect();
                            const itemRect = listItem.getBoundingClientRect();
                            const scrollTop =
                              container.scrollTop +
                              (itemRect.top - containerRect.top) -
                              containerRect.height / 2 +
                              itemRect.height / 2;

                            container.scrollTo({
                              top: scrollTop,
                              behavior: "smooth",
                            });
                          }
                        }, 100);
                      }
                    }}
                    onMouseOut={() => {
                      if (selectedLocationId !== locationId) {
                        setHoveredLocationId(null);
                      }
                    }}
                    title={`マーカー: ${location.spot_name || "記事の場所"}`}
                    icon={{
                      path:
                        "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5" +
                        "M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z",
                      fillColor: getMarkerColor(location),
                      fillOpacity: 1,
                      strokeWeight: 2,
                      strokeColor: "#ffffff",
                      scale: isHovered || isSelected ? 1.8 : 1.5,
                      anchor: { x: 12, y: 22 },
                    }}
                    className={`markerPin jsMarkerPin ${
                      isHovered ? "isHovered" : ""
                    } ${isSelected ? "isSelected" : ""}`}
                  />
                );
              })}
              {activeHoveredLocation && hoveredArticle && infoWindowPosition && (
                <InfoWindow
                  className="infoWindow"
                  position={infoWindowPosition}
                  onCloseClick={() => {
                    setHoveredLocationId(null);
                  }}
                  headerContent={<h4>{activeHoveredLocation.spot_name}</h4>}
                >
                  <a href={company_name ? `/companies/${getCompanyName()}/articles/${hoveredArticle.id}` : `/articles/${hoveredArticle.id}`}>
                    {hoveredArticle.title}
                  </a>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        </div>
      </div>
    </div>
  );
}

ArticleMapViewer.propTypes = {
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      pictures: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string,
          }),
        })
      ),
    })
  ),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      article_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      spot_name: PropTypes.string,
      address: PropTypes.string,
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    })
  ),
  company_name: PropTypes.string,
  articles_hash: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      pictures: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string,
          }),
        })
      ),
    })
  ),
};

ArticleMapViewer.defaultProps = {
  articles: [],
  locations: [],
  company_name: null,
  articles_hash: {},
};

export default renderReact("ArticleMapViewer", ArticleMapViewer);
